/**
 * Gemini Flash Hint Service
 *
 * Provides progressive hints for freeCodeCamp exercises using
 * Google Gemini Flash as the AI backend.
 *
 * Hint levels:
 *   1 – Gentle nudge (conceptual direction, no code)
 *   2 – Relevant docs link + short explanation
 *   3 – YouTube search suggestion + more context
 *   4 – Detailed walkthrough with pseudo-code
 *   5 – Near-complete solution with explanation
 */

import { GEMINI_MODELS } from './config';

const GEMINI_API_BASE = 'https://generativelanguage.googleapis.com/v1beta/models';

/**
 * Build the system prompt that instructs Gemini on which hint level to produce.
 */
function buildSystemPrompt(level) {
  const base = `You are a friendly coding tutor for a learning platform similar to freeCodeCamp.
You NEVER give the full answer on the first try. Instead you guide the learner step-by-step.
Always respond in the SAME language the exercise description is written in.
Keep answers concise and formatted in Markdown.
This is not a live chat. Never ask the learner to send files, paste code, show package.json, confirm details, or reply before you continue.
Do not end with questions like "Can you show me...?", "What do you see?", or "Let me know...".
Use the exercise context already provided and give the best next hint immediately.
When showing code, always use fenced Markdown code blocks with a language tag, for example \`\`\`python, \`\`\`js, \`\`\`html, or \`\`\`css.`;

  const levels = {
    1: `${base}
HINT LEVEL 1 – Gentle nudge:
• Give a single short sentence pointing the learner in the right direction.
• Do NOT reveal any code or solution details.
• Example: "Think about which CSS property controls the space between elements."`,

    2: `${base}
HINT LEVEL 2 – Docs pointer:
• Give a brief explanation (2-3 sentences) of the concept involved.
• Include ONE relevant documentation link (MDN, Python docs, official docs, etc.).
• Still do NOT show the answer.`,

    3: `${base}
HINT LEVEL 3 – Video + context:
• Explain the concept in more detail (a short paragraph).
• Suggest a YouTube search query the learner can use to find a tutorial.
• Format: **YouTube:** search for "<query>".
• You may include a documentation link too.`,

    4: `${base}
HINT LEVEL 4 – Detailed walkthrough:
• Walk through the problem step by step.
• Use pseudo-code or partial code snippets (leave blanks for key parts).
• Explain WHY each step is needed.`,

    5: `${base}
HINT LEVEL 5 – Almost the answer:
• Provide a near-complete solution with detailed comments.
• Explain every line so the learner understands instead of just copying.
• Encourage the learner to type it out themselves.`
  };

  return levels[Math.min(level, 5)] || levels[5];
}

/**
 * Build the user prompt that contains the exercise context.
 */
function buildUserPrompt({ title, description, instructions, tests, code, level }) {
  let prompt = `## Exercise: ${title || 'Untitled'}\n\n`;

  if (description) {
    prompt += `### Description\n${stripHtml(description)}\n\n`;
  }
  if (instructions) {
    prompt += `### Instructions\n${stripHtml(instructions)}\n\n`;
  }
  if (tests && tests.length > 0) {
    prompt += `### Tests the code must pass\n`;
    tests.forEach((t, i) => {
      prompt += `${i + 1}. ${t.text || t.testString || ''}\n`;
    });
    prompt += '\n';
  }
  if (code) {
    prompt += `### Learner's current code\n\`\`\`\n${code}\n\`\`\`\n\n`;
  }

  prompt += `Give me a LEVEL ${level} hint (out of 5). Remember the rules for this level.`;
  return prompt;
}

/**
 * Strip HTML tags from a string (exercise descriptions come as HTML).
 */
function stripHtml(html) {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .trim();
}

/**
 * Call Gemini Flash and return the hint text.
 *
 * @param {object}  params
 * @param {string}  params.apiKey       – Gemini API key
 * @param {string}  params.title        – challenge title
 * @param {string}  params.description  – challenge description HTML
 * @param {string}  params.instructions – challenge instructions HTML
 * @param {Array}   params.tests        – challenge tests
 * @param {string}  params.code         – learner's current code
 * @param {number}  params.level        – hint level (1-5)
 * @returns {Promise<string>} The hint text in Markdown.
 */
export async function fetchHint({
  apiKey,
  title,
  description,
  instructions,
  tests,
  code,
  level
}) {
  if (!apiKey) {
    throw new Error(
      'Gemini API key is missing. Please set it in the chatbot settings.'
    );
  }

  const systemPrompt = buildSystemPrompt(level);
  const userPrompt = buildUserPrompt({
    title,
    description,
    instructions,
    tests,
    code,
    level
  });

  const body = JSON.stringify({
    system_instruction: {
      parts: [{ text: systemPrompt }]
    },
    contents: [
      {
        role: 'user',
        parts: [{ text: userPrompt }]
      }
    ],
    generationConfig: {
      temperature: 0.7,
      maxOutputTokens: 1024
    }
  });

  let lastError = null;

  for (const model of GEMINI_MODELS) {
    const url = `${GEMINI_API_BASE}/${model}:generateContent?key=${apiKey}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body
    });

    // If the model is overloaded or rate-limited, try the next one
    if (response.status === 429 || response.status === 503) {
      lastError = `${model} unavailable (${response.status})`;
      continue;
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err?.error?.message || `Gemini API error: ${response.status}`
      );
    }

    const data = await response.json();
    const text =
      data?.candidates?.[0]?.content?.parts?.[0]?.text ||
      'Sorry, I could not generate a hint right now. Please try again.';
    return text;
  }

  throw new Error(
    `All models are currently experiencing high demand. Please try again later. (${lastError})`
  );
}

export const MAX_HINT_LEVEL = 5;
